import React, { useContext, useEffect, useState } from "react";
import { 
    Button, Container, Divider, Grid, MenuItem, Paper, TextField, 
    Typography, Box, IconButton, Badge, useTheme 
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

// Iconos
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faSave, faUserCheck } from "@fortawesome/free-solid-svg-icons";

// Contextos y Controladores
import UserContext from "../../../navigation/context/userContext";
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import Socials from "./Socials";
import { getProfile, updateProfile, updateProfileImage } from "../../../controllers/customer/ProfileController";
import QUDProgress from "../../../components/progress";
import ResponseComponent from "../../../components/responseComponent";
import { imagesURL } from "../../../apiRoutes/apiBase";

// Assets
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { Avatar } from "@mui/material"; // Usamos el nativo de MUI para más control visual

export default function ClientProfile() {
    const theme = useTheme();
    const { userData } = useContext(UserContext);
    const [isLoading, setIsLoading] = useState(false);

    // Estados del Formulario
    const [name, setName] = useState('');
    const [lastName, setLastName] = useState('');
    const [documentType, setDocumentType] = useState('');
    const [document, setDocument] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [imageProfile, setImageProfile] = useState('');

    // Estados de Error (Simplificados)
    const [errors, setErrors] = useState({ name: false, lastName: false, document: false, phone: false });

    // Estados de Feedback
    const [show, setShow] = useState(false);
    const [success, setSuccess] = useState(false);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        handleGetUserProfile();
    }, []);

    const handleGetUserProfile = async () => {
        setIsLoading(true);
        let response = await getProfile();
        if (response.success === true) {
            setName(response.data.entity.name);
            setLastName(response.data.entity.last_name);
            setDocumentType(response.data.entity.document_type || 'V');
            setDocument(response.data.entity.document_number);
            setPhoneNumber(response.data.entity.phone);
            
            // Validación por si no tiene foto
            const imgPath = response.data.entity.profile_image 
                ? imagesURL + 'users/' + response.data.entity.profile_image 
                : '';
            setImageProfile(imgPath);
        }
        setIsLoading(false);
    }

    // Manejo de subida de imagen (Input oculto)
    const handleImageChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            // Preview local inmediato
            const objectUrl = URL.createObjectURL(file);
            setImageProfile(objectUrl);

            // Subida al servidor
            setIsLoading(true);
            
            // Nota: Asumo que tu controlador espera el archivo directo o un array.
            // Ajusta esto según tu implementación de updateProfileImage
            let response = await updateProfileImage([file]); 
            
            if (response.success) {
                setSuccess(true);
                setTitle('Foto Actualizada');
                setMessage('Tu foto de perfil se ha cambiado correctamente.');
            } else {
                setSuccess(false);
                setTitle('Error');
                setMessage(response.message || 'No se pudo subir la imagen.');
                // Revertir imagen si falla (opcional)
            }
            setShow(true);
            setIsLoading(false);
        }
    };

    const formVerification = () => {
        const newErrors = {
            name: name === '',
            lastName: lastName === '',
            document: document === '',
            phone: phoneNumber === ''
        };
        setErrors(newErrors);
        return Object.values(newErrors).some(val => val === true);
    }

    const handleSubmit = async () => {
        if (formVerification()) return;

        setIsLoading(true);
        // Prefix lo quité porque PhoneInput ya suele manejarlo, o puedes extraerlo del phoneNumber
        let response = await updateProfile(name, lastName, documentType, document, '', phoneNumber);
        
        if (response.success) {
            setSuccess(true);
            setTitle('Perfil Actualizado');
            setMessage(response.message);
        } else {
            setSuccess(false);
            setTitle('Error');
            setMessage(response.message);
        }
        setShow(true);
        setIsLoading(false);
    }

    return (
        <Container maxWidth='xl' sx={{ pb: 5 }}>
            {/* Header y Breadcrumbs */}
            <Box mb={3}>
                <CustomBreadcrumbs returnTo='/' crumbs={[{ name: 'Perfil', link: '/profile' }]} />
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 2 }}>
                    Configuración de Cuenta
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Gestiona tu información personal y seguridad
                </Typography>
            </Box>

            <Grid container spacing={3}>
                
                {/* --- COLUMNA IZQUIERDA: TARJETA DE IDENTIDAD --- */}
                <Grid item xs={12} md={4}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 4, 
                            border: '1px solid', 
                            borderColor: 'divider', 
                            borderRadius: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}
                    >
                        <Box sx={{ position: 'relative', mb: 2 }}>
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={
                                    <IconButton 
                                        color="primary" 
                                        aria-label="upload picture" 
                                        component="label"
                                        sx={{ 
                                            bgcolor: 'background.paper', 
                                            boxShadow: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            '&:hover': { bgcolor: 'background.paper' },
                                            width: 35, height: 35
                                        }}
                                    >
                                        <input hidden accept="image/*" type="file" onChange={handleImageChange} />
                                        <FontAwesomeIcon icon={faCamera} size="xs" />
                                    </IconButton>
                                }
                            >
                                <Avatar
                                    src={imageProfile}
                                    sx={{ 
                                        width: 120, 
                                        height: 120, 
                                        border: `4px solid ${theme.palette.background.paper}`,
                                        boxShadow: '0 0 0 1px #e0e0e0',
                                        bgcolor: theme.palette.primary.light 
                                    }}
                                >
                                    {name ? name.charAt(0) : 'U'}
                                </Avatar>
                            </Badge>
                        </Box>
                        
                        <Typography variant="h6" fontWeight="bold">
                            {name} {lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {userData.user.email}
                        </Typography>
                        
                        <Box 
                            sx={{ 
                                bgcolor: 'rgba(76, 175, 80, 0.1)', 
                                color: 'success.main', 
                                px: 2, py: 0.5, 
                                borderRadius: 10,
                                fontSize: '0.875rem',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <FontAwesomeIcon icon={faUserCheck} /> Cuenta Verificada
                        </Box>
                    </Paper>

                    {/* Componente Socials debajo en móvil, o aquí mismo */}
                    <Box mt={3}>
                        <Socials />
                    </Box>
                </Grid>

                {/* --- COLUMNA DERECHA: FORMULARIO --- */}
                <Grid item xs={12} md={8}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            border: '1px solid', 
                            borderColor: 'divider', 
                            borderRadius: 3 
                        }}
                    >
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold">Información Personal</Typography>
                            <Typography variant="body2" color="text.secondary">Actualiza tus datos básicos</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Nombre"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    error={errors.name}
                                    helperText={errors.name && "Requerido"}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Apellido"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    error={errors.lastName}
                                    helperText={errors.lastName && "Requerido"}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                            </Grid>
                            
                            <Grid item xs={4} sm={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Tipo"
                                    value={documentType}
                                    onChange={(e) => setDocumentType(e.target.value)}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                >
                                    <MenuItem value='V'>V</MenuItem>
                                    <MenuItem value='E'>E</MenuItem>
                                    <MenuItem value='P'>P</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={8} sm={9}>
                                <TextField
                                    fullWidth
                                    label="Documento de Identidad"
                                    value={document}
                                    onChange={(e) => setDocument(e.target.value)}
                                    error={errors.document}
                                    helperText={errors.document && "Requerido"}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ my: 3 }}><Divider /></Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold">Datos de Contacto</Typography>
                            <Typography variant="body2" color="text.secondary">Información privada de contacto</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    disabled
                                    label="Correo Electrónico"
                                    value={userData.user.email}
                                    InputProps={{ sx: { borderRadius: 2, bgcolor: '#f9fafb' } }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    Número Telefónico
                                </Typography>
                                <Box sx={{ 
                                    '& .react-tel-input .form-control': { 
                                        width: '100%', 
                                        height: '56px',
                                        borderColor: errors.phone ? '#d32f2f' : '#c4c4c4',
                                        borderRadius: '8px'
                                    } 
                                }}>
                                    <PhoneInput
                                        country={'ve'}
                                        value={phoneNumber}
                                        onChange={(phone) => setPhoneNumber(phone)}
                                        inputStyle={{ width: '100%' }}
                                    />
                                </Box>
                                {errors.phone && <Typography variant="caption" color="error">Requerido</Typography>}
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                            <LoadingButton
                                loading={isLoading}
                                variant="contained"
                                onClick={handleSubmit}
                                startIcon={<FontAwesomeIcon icon={faSave} />}
                                size="large"
                                sx={{ 
                                    borderRadius: 50, 
                                    px: 4, 
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    boxShadow: 'none'
                                }}
                            >
                                Guardar Cambios
                            </LoadingButton>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <QUDProgress open={isLoading} />
            <ResponseComponent
                show={show}
                success={success}
                title={title}
                message={message}
                actionText={'Aceptar'}
                onClick={() => setShow(false)}
            />
        </Container>
    )
}